import React from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import ErrorFallback from './ErrorBoundary';
import { logger } from '@/utils/logger';

/**
 * Props for the generic wrapper. `fallback`, `onError`, `onReset` and
 * `resetKeys` are forwarded to `react-error-boundary`; any extra props are
 * passed straight through to the underlying <ErrorBoundary>.
 */
interface ErrorBoundaryWrapperProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<FallbackProps>;
  onError?: (error: Error, info: React.ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: unknown[];
  [key: string]: unknown;
}

interface ScreenErrorBoundaryProps {
  children: React.ReactNode;
  screenName: string;
}

interface ComponentErrorBoundaryProps {
  children: React.ReactNode;
  componentName: string;
}

interface APIErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
}

/**
 * Error Boundary Wrapper Component
 * Provides error boundary functionality for any component.
 */
const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  fallback = ErrorFallback,
  onError = (error: Error, info: React.ErrorInfo) => {
    logger.error('Error caught by boundary', { error: error.message, errorInfo: info });
  },
  onReset = () => {
    logger.info('Error boundary reset');
  },
  resetKeys = [],
  ...props
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={fallback}
      onError={onError}
      onReset={onReset}
      resetKeys={resetKeys}
      {...props}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * Screen-specific error boundary with custom fallback.
 */
const ScreenErrorBoundary: React.FC<ScreenErrorBoundaryProps> = ({ children, screenName }) => {
  const customFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-sm w-full bg-red-900/50 border border-red-500 rounded-lg p-4 text-center">
        <h3 className="text-lg font-bold text-red-400 mb-2">
          Error in {screenName}
        </h3>
        <p className="text-sm text-red-300 mb-4">
          {error.message || 'An error occurred'}
        </p>
        <button
          onClick={resetErrorBoundary}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <ErrorBoundaryWrapper fallback={customFallback}>
      {children}
    </ErrorBoundaryWrapper>
  );
};

/**
 * Component-specific error boundary for smaller components.
 */
const ComponentErrorBoundary: React.FC<ComponentErrorBoundaryProps> = ({ children, componentName }) => {
  const customFallback: React.FC<FallbackProps> = ({ resetErrorBoundary }) => (
    <div className="bg-red-50 border border-red-200 rounded p-3 text-center">
      <p className="text-sm text-red-600 mb-2">
        Error in {componentName}
      </p>
      <button
        onClick={resetErrorBoundary}
        className="text-xs bg-red-600 text-white px-2 py-1 rounded"
      >
        Retry
      </button>
    </div>
  );

  return (
    <ErrorBoundaryWrapper fallback={customFallback}>
      {children}
    </ErrorBoundaryWrapper>
  );
};

/**
 * API Error Boundary for components that make API calls.
 */
const APIErrorBoundary: React.FC<APIErrorBoundaryProps> = ({ children, onRetry }) => {
  const customFallback: React.FC<FallbackProps> = ({ resetErrorBoundary }) => (
    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-center">
      <p className="text-sm text-yellow-700 mb-2">
        Connection error
      </p>
      <button
        onClick={() => {
          if (onRetry) onRetry();
          resetErrorBoundary();
        }}
        className="text-xs bg-yellow-600 text-white px-2 py-1 rounded"
      >
        Retry Connection
      </button>
    </div>
  );

  return (
    <ErrorBoundaryWrapper fallback={customFallback}>
      {children}
    </ErrorBoundaryWrapper>
  );
};

export {
  ErrorBoundaryWrapper,
  ScreenErrorBoundary,
  ComponentErrorBoundary,
  APIErrorBoundary,
};
